import React from 'react';
import { Input, InputProps } from '@cloudscape-design/components';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

/**
 * Props for FormInput component
 * Combines Cloudscape Input props with React Hook Form integration
 */
interface FormInputProps<TFieldValues extends FieldValues = FieldValues> extends Omit<
  InputProps,
  'value' | 'onChange' | 'onBlur'
> {
  /** React Hook Form control object */
  control: Control<TFieldValues>;
  /** Field name in the form */
  name: FieldPath<TFieldValues>;
}

/**
 * Wrapper component that integrates Cloudscape Input with React Hook Form
 * Handles the event detail extraction automatically
 *
 * This eliminates boilerplate code across forms by handling the Cloudscape
 * CustomEvent pattern where value is in `detail.value`
 *
 * @example
 * ```tsx
 * <FormInput
 *   control={form.control}
 *   name="title"
 *   placeholder="Enter title"
 * />
 * ```
 */
export const FormInput = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  ...inputProps
}: FormInputProps<TFieldValues>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Input
          {...inputProps}
          value={field.value || ''}
          onChange={({ detail }) => field.onChange(detail.value)}
          onBlur={field.onBlur}
        />
      )}
    />
  );
};
